import moment from 'moment-timezone';

import missionService from '@/modules/companyNeed/services/missionService';
import {
	MISSION_WORKER_ALREADY_ASSIGNED_CODE,
	MissionBulkWorkerStatus,
} from '@/modules/companyNeed/types/missions';
import {
	MissionAction,
	MissionStatus,
} from '@/modules/companyNeed/views/details/missions/types';
import { WorkerMissionStatus } from '@/types/mission';

import type { DateRange } from '@/components/Mission/DateRangeTable/types';
import type {
	OrderAddress,
	Period,
	UpdateMissionPayload,
} from '@/modules/companyNeed/components/modals/createOrder/types';
import type { MissionRowPatch } from '@/modules/companyNeed/store/missionPatchStore';
import type { CompanyNeedAddress } from '@/modules/companyNeed/types/address';
import type {
	AddWorkersToMissionAndMoveProfilesToMissionProposedParams,
	AddWorkersToMissionAndMoveProfilesToMissionProposedResult,
	MissionModalData,
} from '@/modules/companyNeed/types/missions';
import type { MissionPeriod } from '@/modules/companyNeed/views/details/missions/types';
import type { IAddress } from '@/types/address';
import type { RemunerationPeriodicity } from '@/types/remuneration';

const toISOWithTime = (timestamp: number, time: string): string => {
	const parsedTime = moment(time, 'Hmm');
	return moment(timestamp)
		.hours(parsedTime.hours())
		.minutes(parsedTime.minutes())
		.seconds(0)
		.milliseconds(0)
		.toISOString();
};

export const dateRangeToPeriod = ({
	startDate,
	startTime,
	endDate,
	endTime,
	weekendIncluded,
}: DateRange): Period => ({
	// biome-ignore lint/style/noNonNullAssertion: Init Biome
	start: toISOWithTime(startDate!, startTime!),
	// biome-ignore lint/style/noNonNullAssertion: Init Biome
	end: toISOWithTime(endDate!, endTime!),
	weekendIncluded,
});

export const missionPeriodToDateRange = (period: MissionPeriod): DateRange => ({
	startDate: period.start,
	endDate: period.end,
	startTime: moment(period.start).format('Hmm'),
	endTime: moment(period.end).format('Hmm'),
	weekendIncluded: false,
});

export const mapCompanyNeedAddressToOrderAddress = (
	address: CompanyNeedAddress
): OrderAddress => ({
	city: address.city,
	countryCode: address.country ?? 'FR',
	street: address.street,
	complement: address.complement,
	zip: address.zipCode,
	location: [address.longitude, address.latitude],
});

export const mapCompanyNeedAddressToIAddress = (
	address: CompanyNeedAddress
): IAddress => ({
	street: address.street,
	city: address.city,
	zip: address.zipCode,
	complement: address.complement,
	countryCode: address.country ?? 'FR',
	location:
		address.latitude && address.longitude
			? [address.longitude, address.latitude]
			: undefined,
});

export const isMissionSyncWithEvolia = (sendToEvoliaTimestamp?: number) => {
	return !!sendToEvoliaTimestamp;
};

export const isMissionExtended = (extendsOf?: string) => {
	return !!extendsOf;
};

export const worklowCreateMissionAddWorkers = async ({
	missionId,
	workers,
	continueIfMissionUpdateFails = false,
}: AddWorkersToMissionAndMoveProfilesToMissionProposedParams): Promise<AddWorkersToMissionAndMoveProfilesToMissionProposedResult> => {
	// biome-ignore lint/correctness/noUnusedVariables: Init Biome
	const workerIds = workers.map((worker) => worker.workerId);
	let isMissionUpdateSuccessful = true;
	let alreadyAssignedCount = 0;

	try {
		const results = await missionService.addWorkersToMissionBulk(missionId, {
			status: MissionBulkWorkerStatus.Proposed,
			workers,
		});

		alreadyAssignedCount = results.filter(
			(item) => item.code === MISSION_WORKER_ALREADY_ASSIGNED_CODE
		).length;
	} catch (error) {
		isMissionUpdateSuccessful = false;

		if (!continueIfMissionUpdateFails) {
			throw error;
		}
	}

	return { isMissionUpdateSuccessful, alreadyAssignedCount };
};

const nullToUndefined = <T>(value: T | null | undefined): T | undefined =>
	value ?? undefined;

/** Mission details as they are once the command-side accepted the update payload. */
export const applyUpdatePayloadToMissionDetails = (
	mission: MissionModalData,
	payload: UpdateMissionPayload
): MissionModalData => {
	const next: MissionModalData = { ...mission };
	if ('name' in payload) next.name = payload.name ?? mission.name;
	if ('hiringReason' in payload)
		next.hiringReason = nullToUndefined(payload.hiringReason);
	if ('justification' in payload)
		next.explanation = nullToUndefined(payload.justification);
	if ('description' in payload)
		next.description = nullToUndefined(payload.description);
	if ('note' in payload) next.note = nullToUndefined(payload.note);
	if (payload.address) next.address = payload.address;
	if (payload.contractType) next.contractType = payload.contractType;
	if (payload.totalPositions !== undefined)
		next.totalPositions = payload.totalPositions;
	if (payload.showRemuneration !== undefined)
		next.showRemuneration = payload.showRemuneration;
	if ('remunerationPeriodicity' in payload) {
		next.remuneration = payload.remunerationPeriodicity
			? {
					periodicity:
						payload.remunerationPeriodicity as RemunerationPeriodicity,
					minimumAmount: payload.minRemuneration ?? null,
					maximumAmount: payload.maxRemuneration ?? null,
				}
			: undefined;
	}
	if (payload.periods) {
		next.periods = payload.periods.map((period) => ({
			start: Date.parse(period.start),
			end: Date.parse(period.end),
		}));
	}
	return next;
};

const PROJECTION_RETRY_DELAYS_MS = [1000, 2000, 4000, 8000];

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Mission details once the query-side projection reflects a command: the mission
 * exists and, when given, `isProjected` returns true. Resolves to null if it is
 * still not the case after the retries.
 */
export const fetchMissionWhenProjected = async (
	missionId: string,
	isProjected: (mission: MissionModalData) => boolean = () => true
): Promise<MissionModalData | null> => {
	for (const delay of PROJECTION_RETRY_DELAYS_MS) {
		await wait(delay);
		try {
			const mission = await missionService.fetchMissionDetails(missionId);
			if (isProjected(mission)) return mission;
		} catch {
			// Not projected yet, retry after the next delay
		}
	}
	return null;
};

/**
 * Retries `isProjected` with the projection delays until it returns true.
 * Resolves to false if the query-side still lags after the retries.
 */
export const waitUntilProjected = async (
	isProjected: () => Promise<boolean>
): Promise<boolean> => {
	for (const delay of PROJECTION_RETRY_DELAYS_MS) {
		await wait(delay);
		if (await isProjected()) return true;
	}
	return false;
};

interface WorkerStatusImpactParams {
	missionStatus: MissionStatus;
	newWorkerStatus: WorkerMissionStatus;
	/** Confirmed workers once the change is applied. */
	confirmedCount: number;
	totalPositions: number;
}

/**
 * Mission status change implied by a worker status change, as applied by the backend:
 * - a worker accepting a mission to process makes it to validate,
 * - the last expected worker confirmed makes the mission validated.
 */
export const getMissionPatchForWorkerStatus = ({
	missionStatus,
	newWorkerStatus,
	confirmedCount,
	totalPositions,
}: WorkerStatusImpactParams): MissionRowPatch | null => {
	if (
		newWorkerStatus === WorkerMissionStatus.CONFIRMED &&
		missionStatus !== MissionStatus.VALIDATED &&
		totalPositions > 0 &&
		confirmedCount >= totalPositions
	) {
		return {
			status: MissionStatus.VALIDATED,
			removedActions: [MissionAction.VALIDATE],
		};
	}
	if (
		newWorkerStatus === WorkerMissionStatus.ACCEPTED &&
		missionStatus === MissionStatus.TO_PROCESS
	) {
		return { status: MissionStatus.TO_VALIDATE };
	}
	return null;
};

/** Statuses counted by the company need `missions` count: validated and canceled orders are not. */
export const isCountedMissionStatus = (status: string): boolean =>
	status === MissionStatus.TO_PROCESS || status === MissionStatus.TO_VALIDATE;

/** Status returned by the command-side, unless unknown: it would hide the row from status filtered lists. */
export const toKnownMissionStatus = (
	status: string | undefined,
	fallback: MissionStatus
): MissionStatus =>
	Object.values(MissionStatus).includes(status as MissionStatus)
		? (status as MissionStatus)
		: fallback;
